import { AuthBindings, CheckResponse } from "@refinedev/core";
import { User } from "../sdk";
import {ApiSdk} from "./api-sdk";
import {
  ACTIVE_ASSOCIATION_KEY,
  REFRESH_TOKEN_KEY,
  TOKEN_KEY,
} from "./constants";
import * as Sentry from "@sentry/react";
import {refreshAuthToken} from "./safe-refresh-token";


export const authProvider: AuthBindings = {
  login: async ({ username, email, password }) => {
    const failedLoginObject = {
      success: false,
      error: {
        name: "LoginError",
        message: "Invalid username or password",
      },
    };
    if ((username || email) && password) {
      // localStorage.setItem(TOKEN_KEY, username);
      try {
        const body: {
          token: string;
          refreshToken: string;
          tokenExpires: number;
          user: User;
        } = (await ApiSdk.auth.login({
          requestBody: {
            email: email?.toLowerCase(),
            password: password!,
          },
        })) as {
          token: string;
          refreshToken: string;
          tokenExpires: number;
          user: User;
        };

        if (!body?.token) {
          return {
            success: false,
            error: {
              name: "ApiError",
              message: "Invalid response from API",
            },
          };
        }

        localStorage.setItem(TOKEN_KEY, body.token);
        localStorage.setItem(REFRESH_TOKEN_KEY, body.refreshToken);

        return {
          success: true,
          redirectTo: "/",
        };
      } catch (error) {
        Sentry.withScope((scope) => {
          scope.setExtra("function", "login");
          Sentry.captureException(error);
        });
        return failedLoginObject;
      }
    }

    return failedLoginObject;
  },
  logout: async () => {
    localStorage.clear();

    return {
      success: true,
      // redirectTo: "/login",
    };
  },
  check: async () => {
    const notLoggedIn = {
      success: false,
      logout: true,
      authenticated: false,
      redirectTo: "/login",
    };
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (isValidAuthToken()) {
      return {
        success: true,
        authenticated: true,
      };
    }


    if (!refreshToken) {
      return notLoggedIn;
    }

    const isRefreshTokenExpired = isTokenExpired(refreshToken);

    if (isRefreshTokenExpired) {
      removeAuthData();
      return notLoggedIn;
    }

    try {
      await refreshAuthToken();
    } catch (e) {
      return notLoggedIn;
    }

    if (isValidAuthToken()) {
      return {
        success: true,
        authenticated: true,
      };
    }

    return notLoggedIn;
  },
  getPermissions: async () => null,
  getIdentity: async () => {
    const token = localStorage.getItem(TOKEN_KEY);

    if (!token) {
      Sentry.setUser(null);

      return {
        success: false,
        authenticated: false,
        redirectTo: "/login",
        logout: true,
      };
    }

    let body: Record<string, any>;
    try {
      body = await ApiSdk.auth.me();

      if (!body) {
        // Clear Sentry user when API call fails
        Sentry.setUser(null);
        return {
          success: false,
          authenticated: false,
          redirectTo: "/login",
          logout: true,
        };
      }

      Sentry.setUser({
        id: body.id,
        email: body.email,
        username: `${body.firstName} ${body.lastName}`,
      });

      return body;
    } catch (error: any) {
      Sentry.withScope((scope) => {
        scope.setExtra("function", "getIdentity");
        Sentry.captureException(error);
      });
      return {
        success: false,
        authenticated: false,
        redirectTo: "/login",
        logout: true,
      };
    }
  },
  onError: async (error) => {
    Sentry.captureException(error)  ;
    console.error(error);
    return { error, logout: false };
  },
  forgotPassword: async ({ email }) => {
    try {
      await ApiSdk.auth.forgotPassword({
        requestBody: {
          email: email?.toLowerCase(),
        },
      });
      return {
        success: true,
        redirectTo: "/login",
      };
    } catch (error) {
      Sentry.withScope((scope) => {
        scope.setExtra("function", "forgotPassword");
        scope.setExtra("email", email);
        Sentry.captureException(error);
      });
      return {
        success: false,
        error: {
          name: "ForgotPasswordError",
          message: "An error occurred while processing your request",
        },
      };
    }
  },
  updatePassword: async ({ password, repeatPassword, hash }) => {
    try {
      await ApiSdk.auth.resetPassword({
        requestBody: {
          password: password!,
          repeatPassword: repeatPassword!,
          hash: hash!,
        },
      } as any);
      return {
        success: true,
        redirectTo: "/login",
      };
    } catch (error) {

      return {
        success: false,
        error: {
          name: "updatePasswordError",
          message: "An error occurred while processing your request",
        },
      };
    }
  },
};

export const jwtDecode = (token: string) => {
  if (!token) {
    return null;
  }
  try {
    const base64Url = token.split(".")[1];
    if (!base64Url) {
      return null;
    }
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const decodedToken = JSON.parse(window.atob(base64));
    return decodedToken;
  } catch (error) {
    console.error("Error decoding JWT token:", error);
    return null;
  }
};

export const isTokenExpired = (token: string): boolean => {
  const decodedToken = jwtDecode(token);

  if (!decodedToken || !decodedToken.exp) {
    return true;
  }

  const bufferSeconds = 1000 * 10;

  const currentDate = new Date();
  const expDate = new Date(decodedToken.exp * 1000);
  const bufferDate = new Date(currentDate.getTime() + bufferSeconds); // 5000ms = 5 seconds

  return expDate < bufferDate; // Check if token expires within next 5 seconds

};

const removeAuthData = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(ACTIVE_ASSOCIATION_KEY);
};

const isValidAuthToken = () => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (!token) {
    return false;
  }

  return !isTokenExpired(token);
};

